import { ApiError } from './error'
import type { ApiResponse, RequestOptions } from './types'

export interface ClientRequestInit extends RequestInit {
  rawResponse?: boolean
}

/**
 * 核心 HTTP 客户端基类
 * 负责统一的基础请求、身份鉴权头注入、Content-Type 与 FormData 处理、响应解析与业务异常抛出
 */
export class CoreApiClient {
  protected readonly options: RequestOptions

  constructor(options: RequestOptions = {}) {
    this.options = options
  }

  get gateway(): string {
    return this.options.gateway ?? ''
  }

  get token(): string | undefined {
    return this.options.token
  }

  get<T>(path: string, options: ClientRequestInit = {}) {
    return this.request<T>(path, { ...options, method: 'GET' })
  }

  post<T>(path: string, body?: unknown, options: ClientRequestInit = {}) {
    return this.request<T>(path, { ...options, method: 'POST', body: this.serializeBody(body) })
  }

  put<T>(path: string, body?: unknown, options: ClientRequestInit = {}) {
    return this.request<T>(path, { ...options, method: 'PUT', body: this.serializeBody(body) })
  }

  delete<T>(path: string, options: ClientRequestInit = {}) {
    return this.request<T>(path, { ...options, method: 'DELETE' })
  }

  protected serializeBody(body: unknown): BodyInit | undefined {
    if (body === undefined || body === null) {
      return undefined
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return body
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return body
    }
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      return body
    }
    if (typeof body === 'string') {
      return body
    }
    return JSON.stringify(body)
  }

  async request<T>(path: string, init: ClientRequestInit = {}): Promise<T> {
    const isAbsolute = /^https?:\/\//i.test(path)
    const base = this.gateway.replace(/\/+$/, '')
    const url = isAbsolute ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`

    const headers: Record<string, string> = {
      ...(init.headers as Record<string, string> | undefined)
    }

    if (this.token && !headers.Authorization && !headers.authorization) {
      headers.Authorization = `Bearer ${this.token}`
    }

    const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
    if (!isFormData && !headers['Content-Type'] && !headers['content-type'] && init.body && typeof init.body === 'string') {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      ...init,
      headers
    })

    if (response.status === 204) {
      return undefined as unknown as T
    }

    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('application/json')) {
      throw new ApiError(
        'NON_JSON_RESPONSE',
        response.ok ? '接口返回格式异常，请检查网关代理配置' : `接口不可用或网关代理异常 (${response.status})`,
        response.status
      )
    }

    if (init.rawResponse) {
      if (!response.ok) {
        throw new ApiError('HTTP_ERROR', `HTTP ${response.status}`, response.status)
      }
      return (await response.json()) as T
    }

    const payload = (await response.json()) as ApiResponse<T>
    const isBusinessSuccess =
      payload.code === 0 ||
      payload.code === '0' ||
      payload.code === 200 ||
      payload.code === '200' ||
      payload.success === true

    if (!response.ok || !isBusinessSuccess) {
      const serverMsg =
        payload.msg ||
        payload.message ||
        (payload as any)?.error ||
        (payload as any)?.errorMessage ||
        (payload as any)?.detail
      const errCode = payload.code ?? (payload as any)?.status ?? (payload as any)?.errorCode ?? response.status
      const finalMessage = serverMsg
        ? `${serverMsg} (${path})`
        : `[${errCode}] 请求接口失败 (${path})`
      throw new ApiError(errCode, finalMessage, response.status)
    }

    if (payload.data !== undefined) {
      return payload.data
    }
    return payload as unknown as T
  }
}
